import React, { useState } from 'react';
import { 
  Box, 
  Typography, 
  Button, 
  Container, 
  Grid, 
  Card, 
  CardContent,
  Modal,
  IconButton
} from '@mui/material';
import { ArrowForward, PlayArrow, Close } from '@mui/icons-material';
import { havenColors } from '../../theme/theme';

// Import media assets
import HeroVideoThumbnail from '../../assets/images/Frame-389-3.png';
import MonitorsController from '../../assets/images/Monitors-Controller.svg';
import AppStoreButton from '../../assets/images/download-app-store.png';
import GooglePlayButton from '../../assets/images/get-it-on-google-play.png';

import BackgroundMask from '../../assets/images/Mask-Group-24-1.png';
import HvacProImage from '../../assets/images/home_hvac-pro.png';
import HomeownerButtonImage from '../../assets/images/Homeowner-button.png';
import BlueprintBackground from '../../assets/images/blueprint2-left-banner-scaled.jpg';
import CO2StatusCard, { IAQStatusCards } from '../../components/common/CO2StatusCard';

const HomePage: React.FC = () => {
  const [videoPlaying, setVideoPlaying] = useState(true); // Auto-play video on load
  const [modalOpen, setModalOpen] = useState(false);

  const handleVideoClick = () => {
    setVideoPlaying(true);
  };

  const handleModalOpen = () => {
    setModalOpen(true);
  };

    const handleModalClose = () => {
    setModalOpen(false);
  };



 

  return (
    <Box>
      {/* Hero Section - Matching Original Design */}
      <Box
        sx={{
                      minHeight: '60vh', // Reduced by 40% to fix excessive vertical height
          background: `linear-gradient(45deg, ${havenColors.primaryYellow} 0%, ${havenColors.primaryTeal} 70%)`, // 45deg: Yellowflow (left-lower) to Blewstream (right-upper)
          position: 'relative',
          overflow: 'visible', // Changed to visible to allow video to extend beyond bounds
          display: 'flex',
          alignItems: 'center',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundImage: `url(${BackgroundMask})`,
            backgroundSize: 'auto 100%', // Use natural width, scale height to fit container
            backgroundRepeat: 'no-repeat',
            backgroundPosition: 'left center', // Focus on left side to avoid cutting off the person
            opacity: 0.08,
            zIndex: 1
          }
        }}
      >
        {/* Floating Bubble Elements */}
        <Box
          sx={{
            position: 'absolute',
            top: '12%',
            right: '12%',
            width: 50,
            height: 50,
            borderRadius: '50%',
            backgroundColor: 'rgba(255, 255, 255, 0.25)',
            animation: 'float 6s ease-in-out infinite',
            zIndex: 3
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            top: '28%',
            right: '22%',
            width: 35,
            height: 35,
            borderRadius: '50%',
            backgroundColor: 'rgba(255, 255, 255, 0.2)',
            animation: 'float 4s ease-in-out infinite reverse',
            zIndex: 3
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            top: '45%',
            right: '8%',
            width: 70,
            height: 70,
            borderRadius: '50%',
            backgroundColor: 'rgba(255, 255, 255, 0.15)',
            animation: 'float 8s ease-in-out infinite',
            zIndex: 3
          }}
        />

        {/* Mobile Layout - Stacked with centered content */}
              <Box sx={{ 
                display: 'flex',
                flexDirection: 'column',
          alignItems: 'center',
                justifyContent: 'center',
          '@media (min-width: 700px)': {
            display: 'none'
          },
          position: 'relative',
          zIndex: 2,
          py: 4,
          px: 2,
          textAlign: 'center',
          width: '100%'
              }}>
                <Typography 
                  variant="h1" 
                  sx={{ 
              fontSize: '2rem',
                    fontWeight: 700,
                    mb: 2,
                    lineHeight: 1.1,
                    textShadow: '0 2px 4px rgba(0,0,0,0.1)',
              color: 'white'
                  }}
                >
                  Your home, a safe haven.
                </Typography>
                
                <Typography 
                  variant="h5" 
                  sx={{ 
                    mb: 3, 
              fontWeight: 600,
              fontSize: '1rem',
              opacity: 1,
              textShadow: '0 2px 4px rgba(0,0,0,0.2)',
              color: 'white'
                  }}
                >
                  HAVEN helps you achieve healthy indoor air.
                </Typography>

                <Button
                  onClick={handleModalOpen}
                  variant="outlined"
                  size="large"
                  endIcon={<ArrowForward />}
                  sx={{
                    backgroundColor: havenColors.primaryTeal,
                    color: 'white',
                    fontWeight: 600,
              fontSize: '0.9rem',
              px: 2,
                    py: 1.2,
              mb: 4,
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    boxShadow: '0 4px 20px rgba(0,0,0,0.2)',
                    border: `2px solid ${havenColors.primaryTeal}`,
                    '&:hover': {
                      backgroundColor: 'transparent',
                      color: 'white',
                      transform: 'translateY(-3px)',
                      boxShadow: '0 6px 25px rgba(0,0,0,0.3)',
                      border: `2px solid white`,
                      borderColor: 'white'
                    },
                    transition: 'all 0.3s ease'
                  }}
                >
                  Introducing HAVEN
                </Button>

          {/* Video for mobile */}
          <Box sx={{ 
                width: '100%',
            maxWidth: '500px',
            mx: 'auto',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center'
            }}>
              <Box 
                sx={{ 
                  position: 'relative',
                borderRadius: 2,
                overflow: 'hidden',
                aspectRatio: '16/9',
                width: '100%',
                maxWidth: '500px'
              }}
            >
              {!videoPlaying ? (
                <Box
                  onClick={handleVideoClick}
                  sx={{
                    position: 'relative',
                    cursor: 'pointer',
                    width: '100%',
                    height: '100%'
                  }}
                >
                  <Box
                    component="img"
                    src={HeroVideoThumbnail}
                    alt="Introducing HAVEN - Video Thumbnail"
                    sx={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      display: 'block',
                      borderRadius: 2
                    }}
                  />
                  
                  <Box
                    sx={{
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      transform: 'translate(-50%, -50%)',
                      backgroundColor: 'rgba(255, 255, 255, 0.9)',
                      borderRadius: '50%',
                      width: 50,
                      height: 50,
                  display: 'flex',
                  alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer'
                    }}
                  >
                    <PlayArrow 
                      sx={{ 
                        fontSize: 30,
                        color: havenColors.primaryTeal,
                        ml: 0.5
                      }} 
                    />
                  </Box>
                </Box>
              ) : (
                <Box
                  component="iframe"
                  src="https://www.youtube.com/embed/qSsyDgGRX8s?autoplay=1&mute=1&controls=1&modestbranding=1&rel=0"
                  title="HAVEN Introduce to HAVEN"
                  sx={{
                    width: '100%',
                    height: '100%',
                    border: 'none',
                    borderRadius: 2
                  }}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              )}
            </Box>
          </Box>
        </Box>

        {/* Desktop Layout - Side by side with centered content */}
        <Box sx={{ 
          display: 'none',
          '@media (min-width: 700px)': {
            display: 'flex'
          },
          alignItems: 'center', 
          justifyContent: 'center',
          minHeight: '50vh',
          position: 'relative', 
          zIndex: 2, 
                  width: '100%',
          px: { sm: 2, md: 4, lg: 6, xl: 8 },
          py: { sm: 4, md: 0 }
        }}>
          {/* Content Container - perfectly centered */}
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '100%',
            maxWidth: { sm: '800px', md: '1000px', lg: '1200px', xl: '1400px' },
            gap: { sm: 1, md: 2, lg: 4, xl: 6 },
            flexDirection: 'row',
            mx: 'auto' // Ensures perfect centering
          }}>
            {/* Left Side - Text Content */}
            <Box sx={{ 
              flex: '1',
              color: 'white', 
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              textAlign: 'center',
              px: { sm: 1, md: 2, lg: 3, xl: 4 },
              minWidth: 0 // Prevent flex item from overflowing
            }}>
              <Typography 
                variant="h1" 
                sx={{ 
                  fontSize: { 
                    sm: '1.8rem',
                    md: '2rem',
                    lg: '2.6rem',
                    xl: '3.2rem'
                  },
                  fontWeight: 700,
                  mb: 2,
                  lineHeight: 1.1,
                  textShadow: '0 2px 4px rgba(0,0,0,0.1)'
                }}
              >
                Your home, a safe haven.
              </Typography>
              
              <Typography 
                variant="h5" 
                sx={{ 
                  mb: 3, 
                  fontWeight: 600,
                  fontSize: { 
                    sm: '0.9rem',
                    md: '1rem',
                    lg: '1.1rem',
                    xl: '1.2rem'
                  },
                  opacity: 1,
                  textShadow: '0 2px 4px rgba(0,0,0,0.2)'
                }}
              >
                HAVEN helps you achieve healthy indoor air.
              </Typography>

              <Button
                onClick={handleModalOpen}
                variant="outlined"
                size="large"
                endIcon={<ArrowForward />}
                sx={{
                  backgroundColor: havenColors.primaryTeal,
                  color: 'white',
                  fontWeight: 600,
                  fontSize: { sm: '0.8rem', md: '0.9rem', lg: '1rem' },
                  px: { sm: 1.5, md: 2, lg: 3 },
                  py: 1.2,
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  boxShadow: '0 4px 20px rgba(0,0,0,0.2)',
                  border: `2px solid ${havenColors.primaryTeal}`,
                  maxWidth: { sm: '200px', md: '240px', lg: '280px' },
                  alignSelf: 'center',
                  '&:hover': {
                    backgroundColor: 'transparent',
                    color: 'white',
                    transform: 'translateY(-3px)',
                    boxShadow: '0 6px 25px rgba(0,0,0,0.3)',
                    border: `2px solid white`,
                    borderColor: 'white'
                  },
                  transition: 'all 0.3s ease'
                }}
              >
                Introducing HAVEN
              </Button>
            </Box>

            {/* Right Side - Video Content */}
            <Box sx={{ 
              flex: '1',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              minWidth: 0 // Prevent flex item from overflowing
            }}>
                <Box
                  sx={{
                    position: 'relative',
                    borderRadius: 3,
                  overflow: 'hidden',
                    transition: 'all 0.3s ease',
                    zIndex: 4,
                    width: '100%',
                    maxWidth: { 
                    sm: 290,
                    md: 340,
                    lg: 460,
                    xl: 580
                    },
                  aspectRatio: '16/9',
                  transform: 'perspective(1000px) rotateY(-3deg)', // Always 3D in desktop
                    '&:hover': {
                    transform: 'perspective(1000px) rotateY(0deg) scale(1.05)'
                    }
                  }}
                >
                  {!videoPlaying ? (
                  // Custom Thumbnail
                    <Box
                      onClick={handleVideoClick}
                      sx={{
                        position: 'relative',
                        cursor: 'pointer',
                        borderRadius: 3,
                      overflow: 'hidden',
                      width: '100%',
                      height: '100%'
                      }}
                    >
                      <Box
                        component="img"
                        src={HeroVideoThumbnail}
                        alt="Introducing HAVEN - Video Thumbnail"
                        sx={{
                          width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                          display: 'block',
                          borderRadius: 3,
                        boxShadow: '0 20px 40px rgba(0,0,0,0.2)',
                          '&:hover': {
                          boxShadow: '0 25px 50px rgba(0,0,0,0.3)'
                          },
                          transition: 'box-shadow 0.3s ease'
                        }}
                      />
                      
                      {/* Play Button Overlay */}
                      <Box
                        sx={{
                          position: 'absolute',
                          top: '50%',
                          left: '50%',
                          transform: 'translate(-50%, -50%)',
                          backgroundColor: 'rgba(255, 255, 255, 0.9)',
                          borderRadius: '50%',
                        width: { sm: 40, md: 50, lg: 60, xl: 70 },
                        height: { sm: 40, md: 50, lg: 60, xl: 70 },
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          cursor: 'pointer',
                          boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
                          '&:hover': {
                            backgroundColor: 'white',
                            transform: 'translate(-50%, -50%) scale(1.1)'
                          },
                          transition: 'all 0.3s ease'
                        }}
                      >
                        <PlayArrow 
                          sx={{ 
                          fontSize: { sm: 20, md: 24, lg: 28, xl: 36 },
                            color: havenColors.primaryTeal,
                          ml: 0.5
                          }} 
                        />
                      </Box>
                    </Box>
                  ) : (
                  // YouTube Iframe
                    <Box
                      component="iframe"
                    src="https://www.youtube.com/embed/qSsyDgGRX8s?autoplay=1&mute=1&controls=1&modestbranding=1&rel=0"
                      title="HAVEN Introduce to HAVEN"
                      sx={{
                        width: '100%',
                      height: '100%',
                        border: 'none',
                        borderRadius: 3,
                      boxShadow: '0 20px 40px rgba(0,0,0,0.2)',
                      display: 'block'
                      }}
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                    />
                  )}
                </Box>
              </Box>
          </Box>
      </Box>


      </Box>

      {/* Customer Segments Section - Improved Responsive Layout */}
      <Container maxWidth="xl" sx={{ 
        py: 8,
        px: { xs: 2, sm: 3, md: 4 }
      }}>
        <Grid container spacing={4} alignItems="stretch" 
          sx={{ 
            justifyContent: "center",
            flexDirection: { xs: 'column' },
            '@media (min-width: 800px)': {
              flexDirection: 'row'
            }
          }}>
          {/* Middle: Homeowners Section */}
          <Grid item xs={12} 
            sx={{ 
              '@media (min-width: 800px)': {
                maxWidth: '50%',
                flexBasis: '50%'
              }
            }}>
            <Box sx={{ 
              p: 2,
              height: '100%', 
              display: 'flex', 
              alignItems: 'center',
              justifyContent: 'center',
              gap: { xs: 2, md: 2, lg: 3 },
              flexDirection: { xs: 'column', sm: 'row' } // Stack vertically only on extra small screens
            }}>
              <Box
                component="img"
                src={HomeownerButtonImage}
                alt="Homeowners"
                sx={{
                  width: { xs: 150, sm: 180, md: 200, lg: 180 },
                  height: { xs: 220, sm: 280, md: 320, lg: 280 },
                  objectFit: 'contain',
                  flexShrink: 1
                }}
              />
              <Box sx={{ 
                display: 'flex', 
                flexDirection: 'column',
                alignItems: { xs: 'center', sm: 'flex-start' }, // Center on extra small screens, left on small+
                textAlign: { xs: 'center', sm: 'left' }, // Center on extra small screens, left on small+
                flex: 1
              }}>
                <Typography variant="h5" sx={{ 
                  mb: 1, 
                  fontWeight: 700, 
                  color: havenColors.darkGray,
                  fontSize: { xs: '1.2rem', sm: '1.3rem', md: '1.4rem', lg: '1.3rem' }
                }}>
                  Homeowners
                </Typography>
                <Typography variant="body1" sx={{ 
                  mb: 2, 
                  color: 'text.secondary',
                  fontSize: { xs: '0.95rem', sm: '1rem', md: '1.1rem', lg: '0.9rem' },
                  lineHeight: 1.4
                }}>
                  looking to get HAVEN for my home.
                </Typography>
                <Button
                  variant="contained"
                  size="medium"
                  href="/homeowner-start"
                  endIcon={<ArrowForward />}
                  sx={{
                    backgroundColor: havenColors.primaryTeal,
                    px: { xs: 2.5, sm: 3, md: 3.5, lg: 3 },
                    py: { xs: 1, sm: 1.2, md: 1.4, lg: 1 },
                    mb: 1,
                    fontWeight: 600,
                    fontSize: { xs: '0.8rem', sm: '0.9rem', md: '1rem', lg: '0.8rem' },
                    minWidth: { xs: '140px', sm: '160px', md: '180px', lg: '160px' },
                    '&:hover': {
                      backgroundColor: havenColors.darkTeal
                    }
                  }}
                >
                  GET STARTED
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  href="/homeowners"
                  sx={{
                    borderColor: havenColors.darkGray,
                    color: havenColors.darkGray,
                    px: { xs: 2, sm: 2.5, md: 3, lg: 2 },
                    py: { xs: 0.5, sm: 0.6, md: 0.7, lg: 0.5 },
                    fontWeight: 500,
                    fontSize: { xs: '0.7rem', sm: '0.8rem', md: '0.9rem', lg: '0.7rem' },
                    minWidth: { xs: '140px', sm: '160px', md: '180px', lg: '160px' },
                    height: 'auto',
                    whiteSpace: 'normal',
                    lineHeight: 1.2,
                    '&:hover': {
                      backgroundColor: havenColors.darkGray,
                      color: 'white'
                    }
                  }}
                >
                  MORE ON HAVEN<br />FOR HOMEOWNERS
                </Button>
              </Box>
            </Box>
          </Grid>

          {/* Right: HVAC Pros Section */}
          <Grid item xs={12} 
            sx={{ 
              '@media (min-width: 800px)': {
                maxWidth: '50%',
                flexBasis: '50%'
              }
            }}>
            <Box sx={{ 
              p: 2,
              height: '100%', 
              display: 'flex', 
              alignItems: 'center',
              justifyContent: 'center',
              gap: { xs: 2, md: 2, lg: 3 },
              flexDirection: { xs: 'column', sm: 'row' } // Stack vertically only on extra small screens
            }}>
              <Box
                component="img"
                src={HvacProImage}
                alt="HVAC Pros"
                sx={{
                  width: { xs: 150, sm: 180, md: 200, lg: 180 },
                  height: { xs: 220, sm: 280, md: 320, lg: 280 },
                  objectFit: 'contain',
                  flexShrink: 1
                }}
              />
              <Box sx={{ 
                display: 'flex', 
                flexDirection: 'column',
                alignItems: { xs: 'center', sm: 'flex-start' }, // Center on extra small screens, left on small+
                textAlign: { xs: 'center', sm: 'left' }, // Center on extra small screens, left on small+
                flex: 1
              }}>
                <Typography variant="h5" sx={{ 
                  mb: 1, 
                  fontWeight: 700, 
                  color: havenColors.darkGray,
                  fontSize: { xs: '1.2rem', sm: '1.3rem', md: '1.4rem', lg: '1.3rem' }
                }}>
                  HVAC Pros
                </Typography>
                <Typography variant="body1" sx={{ 
                  mb: 2, 
                  color: 'text.secondary',
                  fontSize: { xs: '0.95rem', sm: '1rem', md: '1.1rem', lg: '0.9rem' },
                  lineHeight: 1.4
                }}>
                  who wants to install HAVEN.
                </Typography>
                <Button
                  variant="contained"
                  size="medium"
                  href="https://pro.haveniaq.com"
                  target="_blank"
                  endIcon={<ArrowForward />}
                  sx={{
                    backgroundColor: havenColors.primaryTeal,
                    px: { xs: 2.5, sm: 3, md: 3.5, lg: 3 },
                    py: { xs: 1, sm: 1.2, md: 1.4, lg: 1 },
                    mb: 1,
                    fontWeight: 600,
                    fontSize: { xs: '0.8rem', sm: '0.9rem', md: '1rem', lg: '0.8rem' },
                    minWidth: { xs: '140px', sm: '160px', md: '180px', lg: '160px' },
                    '&:hover': {
                      backgroundColor: havenColors.darkTeal
                    }
                  }}
                >
                  BECOME A PRO
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  href="/become-a-pro"
                  sx={{
                    borderColor: havenColors.darkGray,
                    color: havenColors.darkGray,
                    px: { xs: 2, sm: 2.5, md: 3, lg: 2 },
                    py: { xs: 0.5, sm: 0.6, md: 0.7, lg: 0.5 },
                    fontWeight: 500,
                    fontSize: { xs: '0.7rem', sm: '0.8rem', md: '0.9rem', lg: '0.7rem' },
                    minWidth: { xs: '140px', sm: '160px', md: '180px', lg: '160px' },
                    height: 'auto',
                    whiteSpace: 'normal',
                    lineHeight: 1.2,
                    '&:hover': {
                      backgroundColor: havenColors.darkGray,
                      color: 'white'
                    }
                  }}
                >
                  MORE ON HAVEN<br />FOR PROS
                </Button>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Container>

      {/* Meet HAVEN Section */}
      <Box sx={{ 
        py: 7.5, 
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundImage: `url(${BlueprintBackground})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          opacity: 0.99,
          zIndex: 1
        },
        '&::after': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: `linear-gradient(90deg, rgba(69, 90, 100, 0.8) 0%, rgba(69, 90, 100, 1) 100%)`,
          zIndex: 2
        }
      }}>
        <Container maxWidth="lg" sx={{ 
          position: 'relative', 
          zIndex: 3,
          px: { xs: 2, sm: 3, md: 4 }
        }}>
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={5}>
              <Box
                component="img"
                src={MonitorsController}
                alt="HAVEN Monitors and Controller"
                sx={{
                  width: '100%',
                  height: 'auto',
                  maxWidth: 450,
                  mx: 'auto',
                  display: 'block'
                }}
              />
            </Grid>
            <Grid item xs={12} md={7}>
              <Box sx={{ 
                pl: { md: 2 },
                textAlign: { xs: 'center', md: 'left' },
                display: 'flex',
                flexDirection: 'column',
                alignItems: { xs: 'center', md: 'flex-start' }
              }}>
                <Typography variant="h3" sx={{ 
                  mb: 3, 
                  fontWeight: 700, 
                  color: 'white',
                  fontSize: { xs: '2rem', md: '2.5rem' }
                }}>
                  Meet HAVEN.
                </Typography>
                <Typography variant="h5" sx={{ 
                  mb: 4, 
                  color: 'white',
                  fontWeight: 400,
                  lineHeight: 1.4,
                  fontSize: { xs: '1.1rem', md: '1.3rem' }
                }}>
                  Our solution for whole-home indoor air quality management: for peace of mind, and clean air services year round.
                </Typography>
                <Button
                  variant="outlined"
                  size="large"
                  endIcon={<ArrowForward />}
                  href="/solutions"
                  sx={{
                    borderColor: havenColors.primaryTeal,
                    color: havenColors.primaryTeal,
                    fontWeight: 600,
                    px: 4,
                    py: 1.5,
                    fontSize: '1rem',
                    borderRadius: 2,
                    '&:hover': {
                      backgroundColor: havenColors.primaryTeal,
                      color: 'white',
                      transform: 'translateY(-2px)'
                    }
                  }}
                >
                  MORE ON SOLUTIONS
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Ready to Get Started Section */}
      <Box sx={{ 
        py: 8, 
        background: `linear-gradient(135deg, ${havenColors.primaryTeal} 0%, ${havenColors.darkTeal} 100%)`,
        color: 'white'
      }}>
        <Container maxWidth="lg">
          <Grid container spacing={6} alignItems="center" justifyContent="center">
            <Grid item xs={12} md={7}>
              <Typography variant="h3" sx={{ 
                mb: 2, 
                fontWeight: 700,
                fontSize: { xs: '1.8rem', md: '2.2rem' },
                textAlign: { xs: 'center', md: 'left' }
              }}>
                Ready to get started with HAVEN?
              </Typography>
              <Typography variant="h6" sx={{ 
                mb: 4, 
                opacity: 0.9,
                fontWeight: 400,
                lineHeight: 1.5,
                textAlign: { xs: 'center', md: 'left' }
              }}>
                The first step of every HAVEN journey is to download the HAVEN IAQ app.
              </Typography>
              <Box sx={{ 
                display: 'flex', 
                gap: 2, 
                flexWrap: 'wrap',
                justifyContent: { xs: 'center', md: 'flex-start' }
              }}>
                <Box
                  component="a"
                  href="https://apps.apple.com/us/app/haven-iaq/id1527338018"
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{ display: 'block' }}
                >
                  <Box
                    component="img"
                    src={AppStoreButton}
                    alt="Download on the App Store"
                    sx={{
                      height: 60,
                      width: 'auto',
                      transition: 'transform 0.2s',
                      '&:hover': {
                        transform: 'scale(1.05)'
                      }
                    }}
                  />
                </Box>
                <Box
                  component="a"
                  href="https://play.google.com/store/apps/details?id=com.tzoa.haveniaq"
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{ display: 'block' }}
                >
                  <Box
                    component="img"
                    src={GooglePlayButton}
                    alt="Get it on Google Play"
                    sx={{
                      height: 60,
                      width: 'auto',
                      transition: 'transform 0.2s',
                      '&:hover': {
                        transform: 'scale(1.05)'
                      }
                    }}
                  />
                </Box>
              </Box>
            </Grid>
            <Grid item xs={12} md={5} sx={{ 
              display: 'flex', 
              justifyContent: 'center',
              alignItems: 'center',
              position: 'relative',
              backgroundImage: `url("${process.env.PUBLIC_URL}/Screenshot 2025-07-02 at 11.12.12 PM.png")`,
              backgroundSize: 'contain',
              backgroundPosition: 'center',
              backgroundRepeat: 'no-repeat',
              borderRadius: 2,
              minHeight: '400px',
              width: '100%',
              padding: 1
            }}>
              {/* Background IAQ Status Cards Grid */}
              <Box sx={{ 
                position: 'absolute', 
                top: 0, 
                left: 0, 
                right: 0, 
                bottom: 0, 
                zIndex: 1,
                opacity: 0.6,
                filter: 'blur(2px)'
              }}>
                <IAQStatusCards />
              </Box>
              
              {/* Foreground Animated CO2 Card with Glassmorphism */}
              <Box sx={{
                position: 'relative',
                zIndex: 2,
                backdropFilter: 'blur(20px) saturate(180%)',
                WebkitBackdropFilter: 'blur(20px) saturate(180%)', // Safari support
                backgroundColor: 'rgba(255, 255, 255, 0.15)',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                borderRadius: 3,
                padding: 1,
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3), 0 2px 8px rgba(0, 0, 0, 0.1)',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  borderRadius: 'inherit',
                  background: 'linear-gradient(135deg, rgba(255,255,255,0.2) 0%, rgba(255,255,255,0.05) 100%)',
                  pointerEvents: 'none',
                  zIndex: -1
                }
              }}>
                <CO2StatusCard />
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* HAVEN Blog & News Section */}
      <Box sx={{ 
        py: 10, 
        backgroundColor: '#f8f9fa',
        position: 'relative',
        overflow: 'hidden'
      }}>
        {/* Floating Air Particles Background */}
        <Box sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          pointerEvents: 'none',
          zIndex: 1
        }}>
          {/* Generate multiple floating air particles */}
          {Array.from({ length: 30 }, (_, i) => (
            <Box
              key={i}
              sx={{
                position: 'absolute',
                width: `${Math.random() * 8 + 6}px`,
                height: `${Math.random() * 8 + 6}px`,
                backgroundColor: `rgba(255, 230, 35, ${Math.random() * 0.4 + 0.4})`,
                borderRadius: '50%',
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
                animation: `floatAirParticle${i % 4} ${Math.random() * 5 + 8}s linear infinite`,
                animationDelay: `${Math.random() * 5}s`
              }}
            />
          ))}
        </Box>
        
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 2 }}>
          <Typography variant="h3" align="center" sx={{ 
            mb: 6, 
            fontWeight: 700, 
            color: havenColors.darkGray,
            fontSize: { xs: '2rem', md: '2.5rem' }
          }}>
            HAVEN Blog & News
          </Typography>
          
          <Grid container spacing={4}>
            {[
              {
                title: "No One CAREs About Residential Controls – An Open Letter",
                author: "Kevin Hart",
                date: "September 18, 2024",
                excerpt: "Let's face it: No one CAREs about residential controls - and that needs to change. The current state of residential HVAC controls, especially when it comes to Indoor Air Quality (IAQ), is flat-out inadequate.",
                category: "Industry Insights",
                slug: "no-one-cares-about-residential-controls",
                hasInternalPage: true
              },
              {
                title: "HAVEN Meets TrickleStar: Wireless IAQ Integrations",
                author: "Christina S",
                date: "October 17, 2022",
                excerpt: "HAVEN's Central Air Monitor is now compatible with TrickleStar's Smart Thermostat! Our new wireless IAQ integrations help you maximize savings, optimize comfort, and breathe cleaner air.",
                category: "Technology",
                slug: "haven-meets-tricklestar",
                hasInternalPage: true
              },
              {
                title: "Health effects of Particulate Matter",
                author: "Lucy C",
                date: "January 11, 2022",
                excerpt: "When the air that we breathe is compromised (eg. there are too many particles in the air), we can feel sluggish or tired. Fine particulate matter is particularly dangerous, as particles of 2.5UG or lower are small enough to enter directly into our organs!",
                category: "Health & Safety",
                url: "https://haveniaq.com/2022/01/health-effects-of-particulate-matter/",
                hasInternalPage: false
              }
            ].map((post, index) => {
              const handlePostClick = () => {
                if (post.hasInternalPage) {
                  window.location.href = `/blog/${post.slug}`;
                } else {
                  window.open(post.url, '_blank');
                }
              };
              
              return (
              <Grid item xs={12} md={4} key={index}>
                <Card 
                  onClick={handlePostClick}
                  sx={{ 
                    height: '100%', 
                    borderRadius: 3,
                    boxShadow: '0 4px 16px rgba(0,0,0,0.1)',
                    transition: 'all 0.3s ease',
                    cursor: 'pointer',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: '0 8px 24px rgba(0,0,0,0.15)'
                    }
                  }}>
                  <CardContent sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
                    <Box sx={{ 
                      backgroundColor: havenColors.primaryTeal, 
                      color: 'white', 
                      px: 2, 
                      py: 0.5, 
                      borderRadius: 1, 
                      fontSize: '0.8rem',
                      fontWeight: 600,
                      alignSelf: 'flex-start',
                      mb: 2
                    }}>
                      {post.category}
                    </Box>
                    <Typography variant="h6" sx={{ 
                      mb: 2, 
                      fontWeight: 600, 
                      color: havenColors.darkGray,
                      lineHeight: 1.3,
                      flex: '0 0 auto'
                    }}>
                      {post.title}
                    </Typography>
                    <Typography variant="body2" sx={{ 
                      mb: 3, 
                      color: 'text.secondary',
                      lineHeight: 1.5,
                      flex: '1 1 auto'
                    }}>
                      {post.excerpt}
                    </Typography>
                    <Box sx={{ mt: 'auto' }}>
                      <Typography variant="caption" sx={{ 
                        color: 'text.secondary',
                        fontSize: '0.85rem'
                      }}>
                        By {post.author} • {post.date}
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
              );
            })}
          </Grid>
          
          <Box sx={{ textAlign: 'center', mt: 6 }}>
            <Button
              variant="outlined"
              size="large"
              href="/resources"
              sx={{
                borderColor: havenColors.primaryTeal,
                color: havenColors.primaryTeal,
                fontWeight: 600,
                px: 4,
                py: 1.5,
                '&:hover': {
                  backgroundColor: havenColors.primaryTeal,
                  color: 'white'
                }
              }}
            >
              View All Articles
            </Button>
          </Box>
        </Container>
      </Box>





      {/* Video Modal */}
      <Modal
        open={modalOpen}
        onClose={handleModalClose}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: 2
        }}
      >
        <Box
          sx={{
            position: 'relative',
            width: { xs: '95%', sm: '90%', md: '80%', lg: '70%' },
            maxWidth: 1200,
            aspectRatio: '16/9',
            backgroundColor: 'black',
            borderRadius: 2,
            overflow: 'hidden',
            boxShadow: '0 8px 32px rgba(0,0,0,0.3)'
          }}
        >
          <IconButton
            onClick={handleModalClose}
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              zIndex: 10,
              backgroundColor: 'rgba(0,0,0,0.5)',
              color: 'white',
              '&:hover': {
                backgroundColor: 'rgba(0,0,0,0.7)'
              }
            }}
          >
            <Close />
          </IconButton>
          <Box
            component="iframe"
            src="https://www.youtube.com/embed/qSsyDgGRX8s?autoplay=1&mute=1&start=7"
            title="HAVEN - Introducing HAVEN"
            sx={{
              width: '100%',
              height: '100%',
              border: 'none'
            }}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        </Box>
      </Modal>

      {/* CSS Animations */}
      <style>
        {`
          @keyframes float {
            0%, 100% { transform: translateY(0px); }
            50% { transform: translateY(-20px); }
          }
          
          @keyframes floatAirParticle0 {
            0% { transform: translate(0px, 0px); }
            25% { transform: translate(40px, -50px); }
            50% { transform: translate(-30px, -100px); }
            75% { transform: translate(-50px, -60px); }
            100% { transform: translate(0px, 0px); }
          }
          
          @keyframes floatAirParticle1 {
            0% { transform: translate(0px, 0px); }
            33% { transform: translate(-40px, -80px); }
            66% { transform: translate(50px, -40px); }
            100% { transform: translate(0px, 0px); }
          }
          
          @keyframes floatAirParticle2 {
            0% { transform: translate(0px, 0px); }
            20% { transform: translate(25px, -60px); }
            40% { transform: translate(-20px, -120px); }
            60% { transform: translate(-45px, -80px); }
            80% { transform: translate(35px, -30px); }
            100% { transform: translate(0px, 0px); }
          }
          
          @keyframes floatAirParticle3 {
            0% { transform: translate(0px, 0px); }
            50% { transform: translate(-35px, -90px); }
            100% { transform: translate(0px, 0px); }
          }
        `}
      </style>
    </Box>
  );
};

export default HomePage; 